/**
 * Spiegelt ein fertig gelayoutetes BPMN-Diagramm an der Diagonale und dreht es
 * damit zwischen horizontal und vertikal um - in beide Richtungen.
 *
 * Der Auto-Layouter arbeitet ausschliesslich links-nach-rechts (Spalte =
 * Prozessfortschritt). Statt ihn umzubauen, spiegeln wir das Ergebnis an der
 * Diagonale: x und y tauschen. Das ist eine Isometrie, Ueberlappungen,
 * Kreuzungen und Knickzahl bleiben dadurch unveraendert (gemessen an einem
 * Prozess mit 23 Knoten und 24 Kanten: je 0 / 0 / 16 vor und nach der Drehung).
 *
 * Drei Feinheiten:
 *  - Groessen werden nur bei Pool und Lane mitgedreht. Bei Tasks wuerde aus
 *    100x80 sonst ein hochkanter Kasten 80x100.
 *  - Weil die Groessen bleiben, muss die MITTE gespiegelt werden und nicht die
 *    obere linke Ecke - sonst wandern verschieden geformte Nachbarn um
 *    (w-h)/2 gegeneinander. Siehe mirrorBounds.
 *  - Weil die Knotengroessen bleiben, treffen die Kantenenden nach dem
 *    Spiegeln nicht mehr exakt den Rand (gemessen: 10 von 48 Andockpunkten,
 *    bis 20 px daneben). Sie werden deshalb auf den Rand nachgezogen.
 *
 * Die Datei ist bewusst frei von Abhaengigkeiten und Seiteneffekten: sie nimmt
 * BPMN-XML entgegen und gibt BPMN-XML zurueck.
 */

const NS_BPMN = "http://www.omg.org/spec/BPMN/20100524/MODEL";
const NS_DI = "http://www.omg.org/spec/DD/20100524/DI";
const NS_DC = "http://www.omg.org/spec/DD/20100524/DC";
const NS_BPMNDI = "http://www.omg.org/spec/BPMN/20100524/DI";

/** Vertauscht x und y eines Bounds- oder Waypoint-Knotens. */
function swapPosition(element) {
  const x = element.getAttribute("x");
  const y = element.getAttribute("y");
  if (x === null || y === null) return;
  element.setAttribute("x", y);
  element.setAttribute("y", x);
}

/**
 * Spiegelt ein Rechteck an der Diagonale.
 *
 * Gespiegelt wird die Mitte, nicht die obere linke Ecke. Bei ungleichen
 * Kantenlaengen wandert die Ecke sonst um (w-h)/2 gegen ihre Nachbarn:
 * sichtbar wurde das an den Notizkaesten (145x48) neben ihren Tasks (100x80),
 * die im vertikalen Modell 6 bis 21 Einheiten in den Task hineinragten.
 *
 * Behaelter drehen ihre Groesse mit; fuer sie faellt die Rechnung genau auf
 * den schlichten Tausch von x und y zurueck. Es wird bewusst nicht gerundet,
 * damit zweimaliges Spiegeln exakt an den Ausgangspunkt zurueckfuehrt.
 */
function mirrorBounds(bounds, swapsSize) {
  const x = parseFloat(bounds.getAttribute("x"));
  const y = parseFloat(bounds.getAttribute("y"));
  const width = parseFloat(bounds.getAttribute("width"));
  const height = parseFloat(bounds.getAttribute("height"));
  if (!Number.isFinite(x) || !Number.isFinite(y)) return;
  if (!Number.isFinite(width) || !Number.isFinite(height)) {
    swapPosition(bounds);
    return;
  }
  const newWidth = swapsSize ? height : width;
  const newHeight = swapsSize ? width : height;
  bounds.setAttribute("x", String(y + height / 2 - newWidth / 2));
  bounds.setAttribute("y", String(x + width / 2 - newHeight / 2));
  if (swapsSize) {
    bounds.setAttribute("width", String(newWidth));
    bounds.setAttribute("height", String(newHeight));
  }
}

const NOTE_GAP = 20;

/** Ueberlappen sich zwei Rechtecke? */
function overlaps(a, b) {
  return (
    a.x < b.x + b.width &&
    b.x < a.x + a.width &&
    a.y < b.y + b.height &&
    b.y < a.y + a.height
  );
}

/**
 * Setzt die Notizkaesten nach der Drehung neu neben ihre Aktivitaet.
 *
 * Ein Notizkasten ist breit und flach - 145x48 neben einem Task von 100x80.
 * Sein knapper Abstand nach oben wird beim Spiegeln zum Abstand zur Seite,
 * wo seine Breite nicht mehr hineinpasst. Gemessen an einem Modell mit 24
 * Knoten ragten drei Notizen so bis zu 44 Einheiten in ihren eigenen Task.
 * Reines Spiegeln kann das nicht loesen, weil die Groesse nicht mitdreht.
 *
 * Die Kandidatenliste folgt choose_note_position im Backend, nur nach
 * Ausrichtung sortiert: quer zur Flussrichtung zuerst, damit die Notiz nicht
 * zwischen zwei aufeinanderfolgenden Schritten zu liegen kommt.
 */
function placeAnnotations(notes, boxes, vertical, container) {
  // Innerhalb der Bahn halten, wie clamp_to_container im Backend. Geklemmte
  // Kandidaten werden danach ganz normal auf Ueberlappung geprueft.
  const clamp = (spot, w, h) => {
    if (!container || container.width < w || container.height < h) return spot;
    return {
      x: Math.min(Math.max(spot.x, container.x), container.x + container.width - w),
      y: Math.min(Math.max(spot.y, container.y), container.y + container.height - h),
    };
  };

  for (const note of notes) {
    const anchor = boxes[note.anchorId];
    if (!anchor) continue;
    const { width: w, height: h } = note.box;
    const sideways = [
      { x: anchor.x + anchor.width + NOTE_GAP, y: anchor.y + anchor.height / 2 - h / 2 },
      { x: anchor.x - w - NOTE_GAP, y: anchor.y + anchor.height / 2 - h / 2 },
    ];
    const stacked = [
      { x: anchor.x + anchor.width / 2 - w / 2, y: anchor.y - h - NOTE_GAP },
      { x: anchor.x + anchor.width / 2 - w / 2, y: anchor.y + anchor.height + NOTE_GAP },
    ];
    const candidates = vertical
      ? [...sideways, ...stacked]
      : [...stacked, ...sideways];

    // Weiter weg ruecken, falls alle vier Seiten belegt sind.
    for (let step = 1; step <= 6; step += 1) {
      const push = step * (vertical ? w + NOTE_GAP : h + NOTE_GAP);
      candidates.push(
        vertical
          ? { x: sideways[0].x + push, y: sideways[0].y }
          : { x: stacked[1].x, y: stacked[1].y + push },
      );
    }

    const others = Object.entries(boxes)
      .filter(([id]) => id !== note.id)
      .map(([, box]) => box);
    const placed = candidates.map((candidate) => clamp(candidate, w, h));
    const spot =
      placed.find((candidate) =>
        others.every(
          (other) => !overlaps({ ...candidate, width: w, height: h }, other),
        ),
      ) || placed[0];

    note.box.x = spot.x;
    note.box.y = spot.y;
    boxes[note.id] = note.box;
    note.bounds.setAttribute("x", String(spot.x));
    note.bounds.setAttribute("y", String(spot.y));

    // Die Verbindungslinie neu spannen: Rand des Tasks zu Rand der Notiz.
    if (!note.edge) continue;
    const center = (box) => ({ x: box.x + box.width / 2, y: box.y + box.height / 2 });
    const ends = [
      snapToBorder(center(note.box), anchor),
      snapToBorder(center(anchor), note.box),
    ];
    const waypoints = Array.from(
      note.edge.getElementsByTagNameNS(NS_DI, "waypoint"),
    );
    waypoints.slice(2).forEach((extra) => extra.remove());
    ends.forEach((point, index) => {
      const waypoint = waypoints[index];
      if (!waypoint) return;
      waypoint.setAttribute("x", String(Math.round(point.x)));
      waypoint.setAttribute("y", String(Math.round(point.y)));
    });
  }
}

/** Zieht einen Punkt auf die naechstgelegene Kante des Rechtecks. */
function snapToBorder(point, box) {
  const x = Math.min(Math.max(point.x, box.x), box.x + box.width);
  const y = Math.min(Math.max(point.y, box.y), box.y + box.height);
  const distances = {
    left: Math.abs(x - box.x),
    right: Math.abs(box.x + box.width - x),
    top: Math.abs(y - box.y),
    bottom: Math.abs(box.y + box.height - y),
  };
  const nearest = Object.keys(distances).reduce((a, b) =>
    distances[a] <= distances[b] ? a : b,
  );
  if (nearest === "left") return { x: box.x, y };
  if (nearest === "right") return { x: box.x + box.width, y };
  if (nearest === "top") return { x, y: box.y };
  return { x, y: box.y + box.height };
}

/**
 * @param {string} xml  BPMN-XML mit fertigem Layout
 * @returns {string}    dasselbe Diagramm gedreht, oder das Original bei Fehlern
 */
export function mirrorOrientation(xml) {
  if (!xml) return xml;

  const doc = new DOMParser().parseFromString(xml, "application/xml");
  if (doc.getElementsByTagName("parsererror").length) return xml;

  const plane = doc.getElementsByTagNameNS(NS_BPMNDI, "BPMNPlane")[0];
  if (!plane) return xml;

  // --- Knoten: Position immer, Groesse nur bei Behaeltern ---
  const noteIds = new Set(
    Array.from(doc.getElementsByTagNameNS(NS_BPMN, "textAnnotation")).map(
      (annotation) => annotation.getAttribute("id"),
    ),
  );
  const notes = [];
  let turnsVertical = false;
  let laneBox = null;
  const boxes = {};
  const shapes = Array.from(doc.getElementsByTagNameNS(NS_BPMNDI, "BPMNShape"));
  for (const shape of shapes) {
    const bounds = shape.getElementsByTagNameNS(NS_DC, "Bounds")[0];
    if (!bounds) continue;
    const isContainer = shape.getAttribute("isHorizontal") !== null;
    // Der Pool sagt, wohin gedreht wird - danach richtet sich die Notizseite.
    if (isContainer) turnsVertical = shape.getAttribute("isHorizontal") === "true";
    mirrorBounds(bounds, isContainer);
    if (isContainer) {
      const box = {
        x: parseFloat(bounds.getAttribute("x")),
        y: parseFloat(bounds.getAttribute("y")),
        width: parseFloat(bounds.getAttribute("width")),
        height: parseFloat(bounds.getAttribute("height")),
      };
      // Die engste Bahn begrenzt die Notizen - bei uns die Lane im Pool.
      const area = box.width * box.height;
      if (!laneBox || area < laneBox.width * laneBox.height) laneBox = box;
      // Umschalten statt hart setzen: so dreht dieselbe Funktion auch zurueck.
      shape.setAttribute(
        "isHorizontal",
        shape.getAttribute("isHorizontal") === "true" ? "false" : "true",
      );
    } else {
      const id = shape.getAttribute("bpmnElement");
      const box = {
        x: parseFloat(bounds.getAttribute("x")),
        y: parseFloat(bounds.getAttribute("y")),
        width: parseFloat(bounds.getAttribute("width")),
        height: parseFloat(bounds.getAttribute("height")),
      };
      boxes[id] = box;
      if (noteIds.has(id)) notes.push({ id, box, bounds });
    }
    const label = shape.getElementsByTagNameNS(NS_BPMNDI, "BPMNLabel")[0];
    const labelBounds = label && label.getElementsByTagNameNS(NS_DC, "Bounds")[0];
    if (labelBounds) mirrorBounds(labelBounds, false);
  }

  // --- Kanten: Wegpunkte spiegeln, Enden auf den Knotenrand nachziehen ---
  const flows = {};
  for (const tag of ["sequenceFlow", "messageFlow", "association"]) {
    for (const flow of Array.from(doc.getElementsByTagNameNS(NS_BPMN, tag))) {
      flows[flow.getAttribute("id")] = [
        flow.getAttribute("sourceRef"),
        flow.getAttribute("targetRef"),
      ];
    }
  }

  const edgesByElement = {};
  for (const edge of Array.from(doc.getElementsByTagNameNS(NS_BPMNDI, "BPMNEdge"))) {
    edgesByElement[edge.getAttribute("bpmnElement")] = edge;
    const waypoints = Array.from(edge.getElementsByTagNameNS(NS_DI, "waypoint"));
    waypoints.forEach(swapPosition);

    const label = edge.getElementsByTagNameNS(NS_BPMNDI, "BPMNLabel")[0];
    const labelBounds = label && label.getElementsByTagNameNS(NS_DC, "Bounds")[0];
    if (labelBounds) mirrorBounds(labelBounds, false);

    const [sourceRef, targetRef] = flows[edge.getAttribute("bpmnElement")] || [];
    const ends = [
      [sourceRef, waypoints[0]],
      [targetRef, waypoints[waypoints.length - 1]],
    ];
    for (const [ref, waypoint] of ends) {
      const box = boxes[ref];
      if (!box || !waypoint) continue;
      const snapped = snapToBorder(
        { x: parseFloat(waypoint.getAttribute("x")), y: parseFloat(waypoint.getAttribute("y")) },
        box,
      );
      waypoint.setAttribute("x", String(Math.round(snapped.x)));
      waypoint.setAttribute("y", String(Math.round(snapped.y)));
    }
  }

  // --- Notizen: nach dem Drehen neu an ihre Aktivitaet setzen ---
  for (const note of notes) {
    const link = Object.entries(flows).find(
      ([, [, targetRef]]) => targetRef === note.id,
    );
    if (!link) continue;
    note.anchorId = link[1][0];
    note.edge = edgesByElement[link[0]];
  }
  placeAnnotations(notes, boxes, turnsVertical, laneBox);

  return new XMLSerializer().serializeToString(doc);
}
